import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { buildSliderChartData } from '../../utils/dashboardAssessmentAdapter';
import type { DashboardAssessmentData } from '../../utils/dashboardAssessmentAdapter';
import ContentLoader from '../../components/ContentLoader';
import { CLIENT_CHART_THEME as T } from '../../constants/clientChartTheme';
import '../../assets/css/SliderChart.css';

interface SliderChartProps {
  assessmentData: DashboardAssessmentData | null;
  loading?: boolean;
  error?: string | null;
}

const SliderChart: React.FC<SliderChartProps> = ({
  assessmentData,
  loading = false,
  error = null,
}) => {
  const maxRating = assessmentData?.maxRating ?? 5;

  const { series, categories, hasData } = useMemo(() => {
    if (!assessmentData) {
      return { series: [], categories: [], hasData: false };
    }

    const built = buildSliderChartData(
      assessmentData.selectedCapabilities,
      assessmentData.rows
    );

    const points = built.categories.map((name, index) => ({
      x: name,
      y: built.series[0]?.data[index] ?? 0,
    }));

    return {
      categories: built.categories,
      hasData: built.categories.length > 0,
      series: [
        {
          name: built.series[0]?.name ?? 'Your score',
          data: points,
        },
      ],
    };
  }, [assessmentData]);

  const dynamicHeight = useMemo(() => {
    const rowCount = categories.length;
    const perRow = 78;
    const chrome = 120;
    return Math.max(380, Math.min(960, chrome + rowCount * perRow));
  }, [categories.length]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar' as const,
        height: dynamicHeight,
        stacked: false,
        foreColor: T.text,
        background: 'transparent',
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      theme: {
        mode: 'light' as const,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '50%',
          distributed: false,
          borderRadius: 5,
          dataLabels: {
            position: 'center' as const,
          },
          colors: {
            ranges: [
              {
                from: 0,
                to: Math.max(1, Math.floor(maxRating * 0.2)),
                color: T.primary,
              },
              {
                from: Math.max(2, Math.floor(maxRating * 0.2) + 1),
                to: Math.max(3, Math.floor(maxRating * 0.6)),
                color: '#c9a227',
              },
              {
                from: Math.max(4, Math.floor(maxRating * 0.6) + 1),
                to: maxRating,
                color: '#d64545',
              },
            ],
          },
        },
      },
      dataLabels: {
        enabled: true,
        textAnchor: 'middle' as const,
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
          colors: ['#fff'],
        },
      },
      stroke: {
        width: 1,
        colors: [T.grid],
      },
      grid: {
        borderColor: T.grid,
        padding: {
          top: 28,
          right: 50,
          bottom: 12,
          left: 30,
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
      xaxis: {
        min: 0,
        max: maxRating,
        tickAmount: maxRating,
        decimalsInFloat: 0,
        labels: {
          formatter: (value: string) => {
            const num = Number(value);
            if (!Number.isFinite(num)) return '';
            const rounded = Math.round(num);
            if (Math.abs(num - rounded) > 0.01) return '';
            if (rounded < 0 || rounded > maxRating) return '';
            return String(rounded);
          },
          style: {
            fontSize: '12px',
            fontWeight: 500,
            colors: T.textMuted,
            fontFamily: T.fontFamily,
          },
        },
        title: {
          text: `Capability gap score (1–${maxRating})`,
          style: {
            fontSize: '12px',
            fontWeight: 600,
            color: T.textMuted,
            fontFamily: T.fontFamily,
          },
        },
        axisBorder: {
          color: T.grid,
        },
        axisTicks: {
          color: T.grid,
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 600,
            colors: T.text,
            fontFamily: T.fontFamily,
          },
        },
      },
      tooltip: {
        theme: 'light',
        shared: true,
        intersect: false,
        x: {
          show: true,
        },
        y: {
          formatter(value: number) {
            return `${value} / ${maxRating}`;
          },
        },
        marker: {
          show: true,
        },
        custom({ series, seriesIndex, dataPointIndex }: {
          series: number[][];
          seriesIndex: number;
          dataPointIndex: number;
        }) {
          const score = series[seriesIndex][dataPointIndex];
          const category = categories[dataPointIndex];

          return `
            <div class="apexcharts-tooltip-custom" style="padding: 10px;">
              <div class="capability-tooltip-title">${category}</div>
              <div class="capability-tooltip-item">
                <span>Your Score:</span> <b>${score} / ${maxRating}</b>
              </div>
            </div>
          `;
        },
      },
      legend: {
        show: false,
      },
      title: {
        text: 'Capability gap score',
        align: 'left' as const,
        margin: 12,
        offsetY: 4,
        style: {
          fontSize: '1rem',
          fontWeight: 600,
          color: T.text,
          fontFamily: T.fontFamily,
        },
      },
      responsive: [
        {
          breakpoint: 1000,
          options: {
            chart: {
              height: 300,
            },
            plotOptions: {
              bar: {
                barHeight: '55%',
              },
            },
            dataLabels: {
              style: {
                fontSize: '12px',
              },
            },
            grid: {
              padding: {
                top: 0,
                right: 12,
                bottom: 0,
                left: 4,
              },
            },
            yaxis: {
              labels: {
                maxWidth: 90,
                style: {
                  fontSize: '11px',
                  fontWeight: 500,
                  colors: T.text,
                  fontFamily: T.fontFamily,
                },
              },
            },
            xaxis: {
              labels: {
                style: {
                  fontSize: '11px',
                },
              },
              title: {
                style: {
                  fontSize: '12px',
                },
              },
            },
            title: {
              style: {
                fontSize: '15px',
              },
            },
            legend: {
              fontSize: '12px',
            },
          },
        },
        {
          breakpoint: 600,
          options: {
            plotOptions: {
              bar: {
                barHeight: '62%',
                borderRadius: 4,
              },
            },
            dataLabels: {
              style: {
                fontSize: '11px',
              },
            },
            grid: {
              padding: {
                top: 0,
                right: 6,
                bottom: 0,
                left: 0,
              },
            },
            yaxis: {
              labels: {
                maxWidth: 68,
                offsetX: 0,
                style: {
                  fontSize: '10px',
                  fontWeight: 500,
                  colors: T.text,
                  fontFamily: T.fontFamily,
                },
              },
            },
            xaxis: {
              labels: {
                style: {
                  fontSize: '10px',
                },
              },
              title: {
                style: {
                  fontSize: '11px',
                },
              },
            },
            title: {
              style: {
                fontSize: '14px',
              },
            },
          },
        },
      ],
    }),
    [categories, maxRating, dynamicHeight]
  );

  return (
    <div className="slider-chart-container" style={{ width: '100%' }}>
      {error && <p className="error-message">{error}</p>}
      {loading ? (
        <ContentLoader compact label="Loading capability data…" />
      ) : !hasData ? (
        <p className="dash-chart-status">
          Complete current and future state ratings to see capability gap scores.
        </p>
      ) : (
        <>
          <div
            className="chart-wrapper slider-chart-wrapper"
            style={{ width: '100%', minHeight: dynamicHeight }}
          >
            <Chart
              options={chartOptions}
              series={series}
              type="bar"
              height={dynamicHeight}
              width="100%"
            />
          </div>
          <div className="slider-chart-legend">
            <div className="legend-item">
              <div className="legend-color bar-low" />
              <span>Low</span>
            </div>
            <div className="legend-item">
              <div className="legend-color bar-medium" />
              <span>Medium</span>
            </div>
            <div className="legend-item">
              <div className="legend-color bar-high" />
              <span>High</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SliderChart;
